import React from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import clsx from 'clsx';

// Loading Spinner
export const Spinner = ({ size = 'md', className }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  return (
    <Loader2 className={clsx('animate-spin text-primary-500', sizes[size], className)} />
  );
};

// Loading State
export const LoadingState = ({ message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <Spinner size="lg" />
    <p className="mt-4 text-slate-400">{message}</p>
  </div>
);

// Empty State
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="card p-12 text-center">
    {Icon && <Icon className="w-16 h-16 text-slate-600 mx-auto mb-4" />}
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    {description && <p className="text-slate-400 mb-4">{description}</p>}
    {action}
  </div>
);

// Alert Component
export const Alert = ({ type = 'info', title, message, onClose }) => {
  const styles = {
    info: {
      bg: 'bg-primary-500/10 border-primary-500/20',
      icon: Info,
      iconColor: 'text-primary-400'
    },
    success: {
      bg: 'bg-accent-500/10 border-accent-500/20',
      icon: CheckCircle,
      iconColor: 'text-accent-400'
    },
    warning: {
      bg: 'bg-warning-500/10 border-warning-500/20',
      icon: AlertCircle,
      iconColor: 'text-warning-400'
    },
    error: {
      bg: 'bg-danger-500/10 border-danger-500/20',
      icon: XCircle,
      iconColor: 'text-danger-400'
    }
  };

  const style = styles[type];
  const Icon = style.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={clsx('p-4 rounded-xl border flex items-start gap-3', style.bg)}
    >
      <Icon className={clsx('w-5 h-5 flex-shrink-0 mt-0.5', style.iconColor)} />
      <div className="flex-1">
        {title && <p className="font-medium text-white">{title}</p>}
        {message && <p className="text-sm text-slate-300 mt-1">{message}</p>}
      </div>
      {onClose && (
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <XCircle className="w-5 h-5" />
        </button>
      )}
    </motion.div>
  );
};

// Modal Component
export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={clsx(
          'relative w-full bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl',
          sizes[size]
        )}
      >
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}
        <div className="p-6">{children}</div>
      </motion.div>
    </div>
  );
};

// Confirmation Dialog
export const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirm', danger = false }) => (
  <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
    <p className="text-slate-400 mb-6">{message}</p>
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} className="btn btn-ghost">
        Cancel
      </button>
      <button
        onClick={() => { onConfirm(); onClose(); }}
        className={clsx('btn', danger ? 'btn-danger' : 'btn-primary')}
      >
        {confirmText}
      </button>
    </div>
  </Modal>
);

// Progress Bar
export const ProgressBar = ({ value, max = 100, showLabel = true, color = 'primary' }) => {
  const percentage = Math.min(Math.round((value / max) * 100), 100);
  
  const colors = {
    primary: 'bg-primary-500',
    success: 'bg-accent-500',
    warning: 'bg-warning-500',
    danger: 'bg-danger-500'
  };

  return (
    <div>
      {showLabel && (
        <div className="flex justify-between text-sm mb-1">
          <span className="text-slate-400">Progress</span>
          <span className="text-white font-medium">{percentage}%</span>
        </div>
      )}
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={clsx('h-full rounded-full', colors[color])}
        />
      </div>
    </div>
  );
};

// Avatar Component
export const Avatar = ({ name, src, size = 'md', className }) => {
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg'
  };

  const initials = name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={clsx('rounded-xl object-cover', sizes[size], className)}
      />
    );
  }

  return (
    <div
      className={clsx(
        'rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold',
        sizes[size],
        className
      )}
    >
      {initials}
    </div>
  );
};

// Tooltip Component
export const Tooltip = ({ children, content, position = 'top' }) => {
  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <div className="relative group inline-block">
      {children}
      <div className={clsx(
        'absolute z-50 px-2 py-1 text-xs text-white bg-slate-800 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap',
        positions[position]
      )}>
        {content}
      </div>
    </div>
  );
};

// Skeleton Loader
export const Skeleton = ({ className, variant = 'text' }) => {
  const variants = {
    text: 'h-4 rounded',
    title: 'h-6 rounded',
    circle: 'rounded-full',
    rect: 'rounded-xl'
  };

  return (
    <div className={clsx('skeleton', variants[variant], className)} />
  );
};

// Tabs Component
export const Tabs = ({ tabs, activeTab, onChange }) => (
  <div className="flex gap-1 border-b border-slate-800">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={clsx(
          'px-4 py-3 text-sm font-medium transition-colors relative',
          activeTab === tab.id
            ? 'text-primary-400'
            : 'text-slate-400 hover:text-white'
        )}
      >
        {tab.label}
        {activeTab === tab.id && (
          <motion.div
            layoutId="activeTab"
            className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"
          />
        )}
      </button>
    ))}
  </div>
);

// Dropdown Menu
export const Dropdown = ({ trigger, items, align = 'right' }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const alignments = {
    left: 'left-0',
    right: 'right-0'
  };

  return (
    <div className="relative">
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className={clsx(
              'absolute z-50 mt-2 w-48 bg-slate-900 border border-slate-800 rounded-xl shadow-xl py-1',
              alignments[align]
            )}
          >
            {items.map((item, index) => (
              <button
                key={index}
                onClick={() => { item.onClick?.(); setIsOpen(false); }}
                className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:text-white hover:bg-slate-800 flex items-center gap-2"
              >
                {item.icon && <item.icon className="w-4 h-4" />}
                {item.label}
              </button>
            ))}
          </motion.div>
        </>
      )}
    </div>
  );
};

export default {
  Spinner,
  LoadingState,
  EmptyState,
  Alert,
  Modal,
  ConfirmDialog,
  ProgressBar,
  Avatar,
  Tooltip,
  Skeleton,
  Tabs,
  Dropdown
};
